/** Pure helpers behind `ui/CustomerPicker` (T-41). No React, unit-tested. */

export interface PickerCustomer {
  id: string;
  name: string;
  tax_id?: string | null;
  active?: boolean;
}

/** Lower-case, strip Hebrew points and punctuation that users do not type, collapse spaces. */
export function normalizeForSearch(s: string): string {
  return s
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[֑-ׇ]/g, '')
    .replace(/["'`׳״.,\-_/\\()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Customers matching `query`: names that start with it first, then names that
 * contain it (word order free), then a tax-id match; ties by name. Returns at
 * most `limit` rows plus the total match count.
 */
export function filterCustomers<T extends PickerCustomer>(list: readonly T[], query: string, limit = 50): { rows: T[]; total: number } {
  const q = normalizeForSearch(query);
  const byName = (a: T, b: T) => a.name.localeCompare(b.name, 'he');
  if (!q) {
    const sorted = [...list].sort(byName);
    return { rows: sorted.slice(0, limit), total: sorted.length };
  }
  const words = q.split(' ');
  const scored: { c: T; rank: number }[] = [];
  for (const c of list) {
    const name = normalizeForSearch(c.name);
    let rank = -1;
    if (name.startsWith(q)) rank = 0;
    else if (name.split(' ').some((w) => w.startsWith(q))) rank = 1;
    else if (words.every((w) => name.includes(w))) rank = 2;
    else if (c.tax_id && c.tax_id.replace(/\D/g, '').includes(q.replace(/\D/g, '')) && /\d/.test(q)) rank = 3;
    if (rank >= 0) scored.push({ c, rank });
  }
  scored.sort((a, b) => a.rank - b.rank || byName(a.c, b.c));
  return { rows: scored.slice(0, limit).map((s) => s.c), total: scored.length };
}

/** Merge a server-search page into the cached list without duplicates (server rows win). */
export function mergeCustomers<T extends PickerCustomer>(base: readonly T[], extra: readonly T[]): T[] {
  const map = new Map<string, T>();
  for (const c of base) map.set(c.id, c);
  for (const c of extra) map.set(c.id, c);
  return [...map.values()];
}
